import React from 'react';
import { Box, Typography, Breadcrumbs, Link, Button, useMediaQuery, useTheme } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { Home as HomeIcon, ChevronLeft as ChevronIcon } from '@mui/icons-material';

interface BreadcrumbItem {
  label: string;
  path?: string;
}

interface ActionButton {
  label: string;
  onClick: () => void;
  icon?: React.ReactNode;
  variant?: 'text' | 'outlined' | 'contained';
  color?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';
  disabled?: boolean;
}

interface PageHeaderProps {
  title: string;
  subtitle?: string;
  breadcrumbs?: BreadcrumbItem[];
  actions?: ActionButton[];
  children?: React.ReactNode;
}

const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  subtitle,
  breadcrumbs,
  actions,
  children,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Box
      sx={{
        mb: { xs: 2, sm: 3 },
      }}
    >
      {/* Breadcrumbs */}
      {breadcrumbs && breadcrumbs.length > 0 && (
        <Breadcrumbs
          separator={<ChevronIcon sx={{ fontSize: 18, transform: 'scaleX(-1)' }} />}
          sx={{
            mb: 1.5,
            '& .MuiBreadcrumbs-ol': {
              flexWrap: 'nowrap',
              overflow: 'auto',
              '&::-webkit-scrollbar': {
                display: 'none',
              },
            },
            '& .MuiBreadcrumbs-li': {
              whiteSpace: 'nowrap',
            },
          }}
        >
          <Link
            component={RouterLink}
            to="/"
            sx={{
              display: 'flex',
              alignItems: 'center',
              color: 'text.secondary',
              textDecoration: 'none',
              '&:hover': {
                color: 'primary.main',
              },
            }}
          >
            <HomeIcon sx={{ fontSize: 18, marginInlineStart: 0.5 }} />
            <Typography variant="body2">خانه</Typography>
          </Link>
          {breadcrumbs.map((item, index) => {
            const isLast = index === breadcrumbs.length - 1;

            if (isLast || !item.path) {
              return (
                <Typography
                  key={index}
                  variant="body2"
                  color={isLast ? 'text.primary' : 'text.secondary'}
                  sx={{ fontWeight: isLast ? 500 : 400 }}
                >
                  {item.label}
                </Typography>
              );
            }

            return (
              <Link
                key={index}
                component={RouterLink}
                to={item.path}
                sx={{
                  color: 'text.secondary',
                  textDecoration: 'none',
                  '&:hover': {
                    color: 'primary.main',
                  },
                }}
              >
                <Typography variant="body2">{item.label}</Typography>
              </Link>
            );
          })}
        </Breadcrumbs>
      )}

      {/* Main Header */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 2,
        }}
      >
        {/* Title & Subtitle */}
        <Box>
          <Typography
            variant={isMobile ? 'h5' : 'h4'}
            component="h1"
            sx={{
              fontWeight: 600,
              color: 'text.primary',
              mb: subtitle ? 0.5 : 0,
            }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>

        {/* Action Buttons */}
        {actions && actions.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 1,
              flexShrink: 0,
            }}
          >
            {actions.map((action, index) => (
              <Button
                key={index}
                variant={action.variant || 'contained'}
                color={action.color || 'primary'}
                onClick={action.onClick}
                disabled={action.disabled}
                startIcon={action.icon}
                fullWidth={isMobile}
                size={isMobile ? 'medium' : 'medium'}
                sx={{
                  whiteSpace: 'nowrap',
                }}
              >
                {action.label}
              </Button>
            ))}
          </Box>
        )}
      </Box>

      {/* Additional Content */}
      {children && <Box sx={{ mt: 2 }}>{children}</Box>}
    </Box>
  );
};

export default PageHeader;
